import { useQueryClient } from '@tanstack/react-query'
import React, { useContext, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useNavigate } from '@lib/react-router-dom'

import { AuthSessionContext } from '@context/AuthContext'
import { useModal } from '@context/ModalsContext'

import { SensitiveData } from '@ui/Privacy/SensitiveData'
import { Body2 } from '@ui/Typography'
import { ActionIcon } from '@ui/components/ActionIcon'
import { IconButton } from '@ui/components/Button'
import { Tooltip } from '@ui/components/Tooltip/Tooltip'
import { InfoIcon } from '@ui/icons/Info.icon'
import { ReplayIcon } from '@ui/icons/Replay.icon'
import { TrashIcon } from '@ui/icons/Trash.icon'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@ui/v2/Table/Table'

import { isDocumentManagement2MonthExpired } from '@utils/AccountAccess/accountDocumentStatuses'
import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'
import { formatDate } from '@utils/date.utils'
import { getCentralRowIdConditionally } from '@utils/getItemId'
import { CommonPaths } from '@utils/path'

import { AccountDetailedDto } from '@model/AccountDetailedDto'
import { DocumentCategoryLinkMap } from '@model/DocumentCategories'
import {
  DocumentClientAreaDto,
  isDocumentCategoryAppropriatenessTestStatus,
  isDocumentClientAreaExpiredStatus,
} from '@model/DocumentClientAreaDto'
import {
  isDocumentStatusApproved,
  isDocumentStatusExpired,
  isDocumentStatusPendingApproval,
  isDocumentStatusRejected,
} from '@model/DocumentStatus'

import { Box } from '@shared/components/Box'
import { Loading } from '@shared/components/Loading/Loading'
import { ConfirmModal } from '@shared/modals/Confirm.modal'

import { DocumentBadgeStatus } from './DocumentBadgeStatus'
import { UploadedDocumentStatusHistoryModal } from './UploadedDocumentStatusHistory'
import { isUploadLocked } from './UploadedDocuments'

interface UploadedDocumentsProps {
  documents: DocumentClientAreaDto[]
  forceRefresh(): void
  readOnlyDocumentIds: string[]
}

export const UploadedDocumentsTable: React.FC<UploadedDocumentsProps> = ({
  documents,
  forceRefresh,
  readOnlyDocumentIds,
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const apiClient = useApiClient(ClientApiClient)
  const queryClient = useQueryClient()

  const [auth] = useContext(AuthSessionContext)
  const dateFormat = auth?.dateFormatType?.name

  const [isLoading, setLoading] = useState(false)

  const statusHistoryModal = useModal(UploadedDocumentStatusHistoryModal)

  const deleteConfirmModal = useModal(ConfirmModal)

  const renew = (doc: DocumentClientAreaDto) => {
    const categoryId = doc.category.id as keyof typeof DocumentCategoryLinkMap
    const route = DocumentCategoryLinkMap[categoryId]
    if (!route) return
    navigate(`${CommonPaths.DocumentManagement}/${route}`, {
      state: { document: doc, documentCategory: doc.category, documentCategoryType: doc.type },
    })
  }

  const askDelete = (doc: DocumentClientAreaDto) => {
    deleteConfirmModal.open({
      title: t('Profile.Uploaded Document'),
      description: t('Profile.Do you really want to delete this document?'),
      secondaryLabel: t('Cancel'),
      primaryLabel: t('Confirm'),
      onConfirm: () => confirmDelete(doc),
    })
  }

  const confirmDelete = async (doc: DocumentClientAreaDto) => {
    try {
      setLoading(true)
      await apiClient.deleteDocument(doc.id)
      await queryClient.invalidateQueries({ queryKey: ['document'] })
      forceRefresh()
    } finally {
      setLoading(false)
    }
  }

  return (
    <Loading isLoading={isLoading} showLoadingIcon>
      <Table noResults={documents.length === 0}>
        <TableHeader>
          <TableRow>
            <TableHead>{t('Profile.Document Category')}</TableHead>
            <TableHead>{t('Profile.Document Type')}</TableHead>
            <TableHead>{t('Profile.Document Name')}</TableHead>
            <TableHead>{t('Profile.Upload date')}</TableHead>
            <TableHead>{t('Profile.Expiry date')}</TableHead>
            <TableHead className='has-text-centered'>{t('Profile.Status')}</TableHead>
            <TableHead />
            <TableHead />
          </TableRow>
        </TableHeader>

        <TableBody>
          {documents.map((doc, index) => {
            const uploadedDate =
              doc.uploadedDate && formatDate(doc.uploadedDate, { formatType: dateFormat })
            const expiryDate =
              doc.expiryDate && formatDate(doc.expiryDate, { formatType: dateFormat })

            const canRenew = isDocumentRenewableAllowed(doc)
            const canDelete = isDocumentDeleteAllowed(readOnlyDocumentIds)(doc)
            const soonToExpire =
              !!doc.expiryDate &&
              isDocumentManagement2MonthExpired(doc.expiryDate) &&
              !isDocumentClientAreaExpiredStatus(doc)

            return (
              <TableRow
                key={doc.id}
                {...getCentralRowIdConditionally(index, documents.length, 'uploaded-documents')}
              >
                <TableCell>{doc.category.name}</TableCell>

                <TableCell>
                  <SensitiveData>{doc.type.name}</SensitiveData>
                </TableCell>

                <TableCell>
                  <SensitiveData>{doc.name}</SensitiveData>
                </TableCell>

                <TableCell>
                  <SensitiveData>{uploadedDate}</SensitiveData>
                </TableCell>

                <TableCell>
                  {expiryDate && (
                    <SensitiveData>
                      <Box flex align='center' gap='x8'>
                        {isDocumentClientAreaExpiredStatus(doc) ? (
                          <SensitiveData>
                            <Body2 color={'text-danger-base-em'}>{expiryDate}</Body2>
                          </SensitiveData>
                        ) : (
                          <SensitiveData>{expiryDate}</SensitiveData>
                        )}
                        {soonToExpire && (
                          <Tooltip autoHide={2000} content={t('Expires soon')} direction='topRight'>
                            <ActionIcon icon={InfoIcon} size='x20' />
                          </Tooltip>
                        )}
                      </Box>
                    </SensitiveData>
                  )}
                </TableCell>

                <TableCell className='has-text-centered'>
                  <DocumentBadgeStatus
                    {...doc.status}
                    onClick={() => statusHistoryModal.open({ documentClientArea: doc })}
                  />
                </TableCell>
                {canRenew && (
                  <TableCell>
                    <IconButton.Ghost icon={ReplayIcon} onClick={() => renew(doc)} />
                  </TableCell>
                )}
                {canDelete && (
                  <TableCell>
                    <IconButton.Ghost icon={TrashIcon} onClick={() => askDelete(doc)} />
                  </TableCell>
                )}
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </Loading>
  )
}

const isDocumentRenewableAllowed = (
  document: DocumentClientAreaDto,
  account?: AccountDetailedDto
): boolean => {
  if (
    isDocumentStatusExpired(document.status.id) &&
    !isDocumentCategoryAppropriatenessTestStatus(document) &&
    !isUploadLocked(document, account)
  ) {
    return true
  }
  if (
    isDocumentStatusPendingApproval(document.status.id) &&
    !isDocumentCategoryAppropriatenessTestStatus(document) &&
    !isUploadLocked(document, account)
  ) {
    return true
  }
  if (
    isDocumentStatusRejected(document.status.id) &&
    !isDocumentCategoryAppropriatenessTestStatus(document) &&
    !isUploadLocked(document, account)
  ) {
    return true
  }
  if (
    isDocumentStatusApproved(document.status.id) &&
    isDocumentManagement2MonthExpired(document.expiryDate) &&
    !isDocumentCategoryAppropriatenessTestStatus(document) &&
    !isUploadLocked(document, account)
  ) {
    return true
  }
  return false
}

const isDocumentDeleteAllowed =
  (readOnlyDocumentIds: string[]) => (document: DocumentClientAreaDto) =>
    !readOnlyDocumentIds?.includes(document.id) &&
    isDocumentStatusPendingApproval(document.status.id)
